import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { alpha } from "@mui/material/styles";

import {
  avatarNoneValue,
  avatarOptionGroups,
  avatarOptionLabels,
  buildDiceBearAvatarUrl,
  getDefaultAvatarConfig,
  getStoredAvatarConfig,
  getUserAvatarSeed,
  normalizeAvatarColor,
  normalizeAvatarConfig,
} from "../../../lib/userAvatar";

function getRandomFrom(options) {
  return options[Math.floor(Math.random() * options.length)];
}

function randomizeAvatarConfig(user) {
  const nextConfig = getDefaultAvatarConfig(user);

  avatarOptionGroups.forEach((group) => {
    group.fields.forEach((field) => {
      if (field.optional && Math.random() < 0.45) {
        nextConfig.options[field.name] = "";
        return;
      }

      nextConfig.options[field.name] = getRandomFrom(field.options);
    });
  });

  nextConfig.seed = `${getUserAvatarSeed(user)}-${Date.now().toString(36)}`;

  return nextConfig;
}

function AvatarColorOption({ color, name, selected, onSelect }) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={`Selecionar cor ${color}`}
      onClick={() => onSelect(name, color)}
      sx={(theme) => ({
        bgcolor: `#${color}`,
        border: selected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.border.default}`,
        borderRadius: "50%",
        boxShadow: selected ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}` : "none",
        cursor: "pointer",
        height: 28,
        p: 0,
        width: 28,
      })}
    />
  );
}

function AvatarOptionField({ field, value, onChange }) {
  if (field.type === "color") {
    return (
      <Box>
        <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 500, lineHeight: "18px", mb: 1 }}>
          {field.label}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {field.options.map((color) => (
            <AvatarColorOption
              key={color}
              color={color}
              name={field.name}
              selected={normalizeAvatarColor(value) === color}
              onSelect={onChange}
            />
          ))}
        </Box>
      </Box>
    );
  }

  const options = field.optional ? [avatarNoneValue, ...field.options] : field.options;

  return (
    <TextField
      select
      fullWidth
      label={field.label}
      value={value || avatarNoneValue}
      onChange={(event) => onChange(field.name, event.target.value === avatarNoneValue ? "" : event.target.value)}
      sx={(theme) => ({
        "& .MuiInputLabel-root": {
          color: theme.palette.text.primary,
          fontSize: 13,
          fontWeight: 600,
          transform: "none",
          position: "relative",
          mb: 0.75,
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          minHeight: 42,
        },
        "& .MuiSelect-select": {
          alignItems: "center",
          display: "flex",
          py: "10px !important",
        },
        "& legend": {
          display: "none",
        },
        "& fieldset": {
          top: 0,
        },
      })}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {avatarOptionLabels[option] || option}
        </MenuItem>
      ))}
    </TextField>
  );
}

export default function UserAvatarCustomizerDialog({
  onClose,
  onSaveAvatar,
  open,
  saving = false,
  user,
}) {
  const [config, setConfig] = useState(() => normalizeAvatarConfig(getStoredAvatarConfig(user), user));
  const previewUrl = buildDiceBearAvatarUrl(config);

  useEffect(() => {
    if (!open) return;

    setConfig(normalizeAvatarConfig(getStoredAvatarConfig(user), user));
  }, [open, user]);

  function handleOptionChange(name, value) {
    setConfig((current) => ({
      ...current,
      options: {
        ...current.options,
        [name]: value,
      },
    }));
  }

  async function handleSave() {
    await onSaveAvatar?.(normalizeAvatarConfig(config, user));
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: { xs: "calc(100vh - 32px)", sm: "calc(100vh - 64px)" },
          overflow: "hidden",
          width: { xs: "calc(100% - 32px)", md: 980, lg: 1060 },
        },
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.surface.subtle,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          fontSize: 16,
          fontWeight: 600,
          justifyContent: "space-between",
          lineHeight: "24px",
          px: 3,
          py: 1.5,
        })}
      >
        Personalizar avatar
        <IconButton aria-label="Fechar personalização de avatar" onClick={onClose} sx={{ height: 36, width: 36 }}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", md: "240px minmax(0, 1fr)", lg: "260px minmax(0, 1fr)" },
          maxHeight: { xs: "calc(100vh - 104px)", sm: "calc(100vh - 136px)" },
          overflowY: "auto",
          px: 3,
          pb: 2.5,
          pt: "24px !important",
        }}
      >
        <Box
          sx={(theme) => ({
            alignSelf: "start",
            bgcolor: theme.palette.surface.subtle,
            border: `1px solid ${theme.palette.border.default}`,
            borderRadius: 2,
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            p: 2,
            position: { md: "sticky" },
            top: 0,
          })}
        >
          <Box
            key={previewUrl}
            component="img"
            src={previewUrl}
            alt=""
            sx={{ borderRadius: "50%", flexShrink: 0, height: 140, width: 140 }}
          />
          <Typography sx={{ color: "text.primary", fontSize: 14, fontWeight: 600, lineHeight: "20px", textAlign: "center" }}>
            Seu avatar interno
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 12, lineHeight: "18px", textAlign: "center" }}>
            Ele ajuda a identificar você em fluxos internos e ações da conta.
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setConfig(randomizeAvatarConfig(user))}
            sx={{ borderRadius: 999, fontSize: 12, fontWeight: 600, minHeight: 36, mt: 0.5 }}
          >
            Gerar variação
          </Button>
        </Box>

        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          {avatarOptionGroups.map((group) => (
            <Box key={group.title}>
              <Typography sx={{ color: "text.primary", fontSize: 14, fontWeight: 600, lineHeight: "20px", mb: 1.5 }}>
                {group.title}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(260px, 1fr))" },
                }}
              >
                {group.fields.map((field) => (
                  <AvatarOptionField
                    key={field.name}
                    field={field}
                    value={config.options[field.name]}
                    onChange={handleOptionChange}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>

        <Box sx={{ display: "flex", gridColumn: { md: 2 }, justifyContent: "flex-end", mt: 0.5 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              borderRadius: 999,
              boxShadow: "none",
              fontSize: 14,
              fontWeight: 600,
              height: 40,
              minWidth: 88,
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
